import { useEffect } from 'react';
import type { JSX } from 'react';

import type { ThemeId } from '../lib/storage';

// ---------------------------------------------------------------------------
// ThemeSettingsSheet
// ---------------------------------------------------------------------------
//
// UI 刷新 (§7.3) のテーマ切替シート。アプリ全体の右上メニュー → 「テーマ」から
// 開かれ、画面下部からスライドアップしてくるボトムシート。
// 2 つのテーマカード (NUDE MOCHA / SAGE BLUR) を並べ、タップで即座に
// `<html data-theme>` を切り替える。Apply ボタンは無く、即時反映 → 「完了」で
// 閉じるだけ。

interface ThemePreviewSpec {
  readonly id: ThemeId;
  readonly nameJa: string;
  readonly nameEn: string;
  readonly subtitle: string;
  /** プレビュー枠の背景。CSS 値そのまま。 */
  readonly bg: string;
  /** プレビュー枠内の「自分のバブル」の塗り。 */
  readonly ownBubble: string;
  /** プレビュー枠内の「相手のバブル」の塗り。 */
  readonly otherBubble: string;
  /** 「相手のバブル」の border 表現。 */
  readonly otherBorder: string;
  /** sage ではバブル裏に backdrop-filter を当てる。 */
  readonly glass: boolean;
}

const THEME_PREVIEWS: readonly ThemePreviewSpec[] = [
  {
    id: 'pastel',
    nameJa: 'ダスティ パステル',
    nameEn: 'DUSTY PASTEL',
    subtitle: 'くすみピンク × ラベンダー × ベージュ',
    bg: 'linear-gradient(135deg, #FFF0F2 0%, #F5ECF7 50%, #FAF0E6 100%)',
    ownBubble: '#D9A0A0',
    otherBubble: '#FFFFFF',
    otherBorder: '1px solid #EADBD9',
    glass: true,
  },
  {
    id: 'mocha',
    nameJa: 'ヌード モカ',
    nameEn: 'NUDE MOCHA',
    subtitle: 'ベージュ × エスプレッソ × キャラメル',
    bg: 'radial-gradient(circle at 0% 0%, #FAF5EE 0%, #F5EDE3 60%)',
    ownBubble: '#C8956A',
    otherBubble: '#FFFFFF',
    otherBorder: '1px solid #EFE3D2',
    glass: false,
  },
  {
    id: 'sage',
    nameJa: 'セージ ブラー',
    nameEn: 'SAGE BLUR',
    subtitle: 'セージ × ダスティローズ × グラス',
    bg: 'linear-gradient(135deg, #EFF3EC 0%, #F5E8E8 45%, #E5DDEE 100%)',
    ownBubble: 'linear-gradient(135deg, #7FA084, #5C7A62)',
    otherBubble: 'rgba(255, 255, 255, 0.75)',
    otherBorder: '1px solid rgba(255, 255, 255, 0.9)',
    glass: true,
  },
];

export interface ThemeSettingsSheetProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly theme: ThemeId;
  readonly onChange: (next: ThemeId) => void;
}

export function ThemeSettingsSheet({
  isOpen,
  onClose,
  theme,
  onChange,
}: ThemeSettingsSheetProps): JSX.Element {
  // Esc で閉じる
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  return (
    <div
      className={
        isOpen ? 'theme-sheet-overlay theme-sheet-overlay--open' : 'theme-sheet-overlay'
      }
      onClick={onClose}
      aria-hidden={!isOpen}
    >
      <div
        className="theme-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="theme-sheet-title"
      >
        <div className="theme-sheet__handle" aria-hidden="true" />
        <div className="theme-sheet__title" id="theme-sheet-title">テーマ</div>
        <div className="theme-sheet__sub">アプリ全体の見た目を変更できます</div>

        {THEME_PREVIEWS.map((preview) => {
          const active = theme === preview.id;
          return (
            <button
              key={preview.id}
              type="button"
              className={
                active ? 'theme-card theme-card--active' : 'theme-card'
              }
              onClick={() => onChange(preview.id)}
              aria-pressed={active}
            >
              <div
                className="theme-card__preview"
                style={{ background: preview.bg }}
                aria-hidden="true"
              >
                <span
                  className="theme-card__bubble theme-card__bubble--other"
                  style={{
                    background: preview.otherBubble,
                    border: preview.otherBorder,
                    backdropFilter: preview.glass ? 'blur(8px)' : undefined,
                    WebkitBackdropFilter: preview.glass ? 'blur(8px)' : undefined,
                  }}
                />
                <span
                  className="theme-card__bubble theme-card__bubble--own"
                  style={{ background: preview.ownBubble }}
                />
              </div>
              <div className="theme-card__meta">
                <div className="theme-card__name">
                  {preview.nameJa}
                  <span className="theme-card__name-en"> · {preview.nameEn}</span>
                </div>
                <div className="theme-card__subtitle">{preview.subtitle}</div>
              </div>
              <div className="theme-card__check" aria-hidden="true">
                {active && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            </button>
          );
        })}

        <div className="theme-sheet__hint">
          ヒント：テーマ切替はアプリのどの画面からでも、右上のメニュー → テーマから変更できます。
        </div>

        <button type="button" className="theme-sheet__done" onClick={onClose}>
          完了
        </button>
      </div>
    </div>
  );
}
